<template>
  <v-card
    class="mt-5 helmi-card"
    outlined
    elevation="0"
  >
    <v-card-text class="pb-0">
      <div class="helmi-card-title mb-2">
        Toiminnot
      </div>
    </v-card-text>
    <div class="action-buttons px-4 pb-4">
      <v-alert
        v-if="!!message"
        type="error"
        text
        class="mx-0 mt-0 mb-3"
      >
        {{ message }}
      </v-alert>

      <v-btn
        outlined
        color="primary"
        :disabled="locked || !body.events"
        :loading="loadingPdf"
        @click="pdf()"
      >
        Lataa kauppakirjan luonnos
      </v-btn>

      <v-btn
        v-if="cancelledRemotely"
        depressed
        color="primary"
        :loading="resetting"
        @click="reset()"
      >
        Aloita uudelleen
      </v-btn>

      <v-btn
        depressed
        color="primary"
        :disabled="locked"
        :loading="saving"
        @click="send()"
      >
        Tallenna luonnos
      </v-btn>

      <v-btn
        depressed
        color="primary"
        :disabled="locked || !sendable"
        @click="approvalDialog = true"
      >
        Lähetä valmisteluun
      </v-btn>

      <v-btn
        depressed
        color="primary"
        :disabled="!signingCanStart"
        @click="startSigningDialog = true"
      >
        Käynnistä allekirjoitusvaihe
      </v-btn>

      <v-btn
        outlined
        color="red"
        :disabled="!cancellable"
        @click="cancelDialog = true"
      >
        Keskeytä kauppa
      </v-btn>
    </div>

    <v-dialog v-model="approvalDialog" max-width="500">
      <v-card>
        <v-card-title>Lähetä pankkien valmisteluun?</v-card-title>
        <v-card-text class="pb-2">
          Lähettämällä kauppakirjan pankkien valmisteluun vahvistat, että olet
          hyväksyttänyt kauppakirjan luonnoksen kaupan osapuolilla.

          <v-checkbox
            v-model="automaticSigningProcess"
            dense
            hide-details
            class="mt-4"
            label="Aloita allekirjoitusvaihe automaattisesti pankkien hyväksynnän jälkeen"
          />
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn
            outlined
            color="primary"
            @click="approvalDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            depressed
            color="primary"
            :loading="submitting"
            @click="submit()"
          >
            Kyllä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="cancelDialog" max-width="500">
      <v-card>
        <v-card-title>Keskeytä kauppa</v-card-title>
        <v-card-text class="pb-0">
          <v-text-field
            v-model="cancelReason"
            label="Keskeytyksen syy"
          />
        </v-card-text>

        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn
            outlined
            color="primary"
            @click="cancelDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            depressed
            color="primary"
            :disabled="!cancelReason"
            :loading="cancelling"
            @click="cancel()"
          >
            Kyllä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="startSigningDialog" max-width="500">
      <v-card>
        <v-card-title>Käynnistä allekirjoitusvaihe</v-card-title>
        <v-card-text class="pb-2">
          Oletko varma että haluat käynnistää allekirjoitusvaiheen?
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-spacer/>
          <v-btn
            outlined
            color="primary"
            @click="startSigningDialog = false"
          >
            Peruuta
          </v-btn>
          <v-btn
            depressed
            color="primary"
            :loading="startingSigning"
            @click="startSigning()"
          >
            Kyllä
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { apiPost } from '../../api';
import download from 'downloadjs'; // eslint-disable-line

export default {
  props: {
    body: {
      type: Object,
      default: () => {},
    },
    phases: {
      type: Array,
      default: () => [],
    },
    locked: {
      type: Boolean,
      default: () => false,
    },
    sendable: {
      type: Boolean,
      default: () => false,
    },
    removePledges: {
      type: Boolean,
      default: () => false,
    },
  },
  data: () => ({
    cancelled: false,
    resetting: false,
    cancelReason: '',
    approvalDialog: false,
    cancelDialog: false,
    startSigningDialog: false,
    message: '',
    loadingPdf: false,
    saving: false,
    cancelling: false,
    submitting: false,
    startingSigning: false,
    automaticSigningProcess: true,
  }),
  computed: {
    cancelledRemotely() {
      if (!this.body?.events?.length) return false;

      return this.body.events[this.body.events.length - 1]?.tradePhase === 'CANCELLED';
    },
    signingCanStart() {
      if (!this.body?.events?.length) return false;

      return this.body.events[this.body.events.length - 1]?.tradePhase === 'APPROVAL_PHASES_COMPLETED';
    },
    cancellable() {
      if (!this.body?.events?.length) return false;

      const currentPhase = this.phases
        .find(phase => phase.tradePhase
          === this.body.events[this.body.events.length - 1]?.tradePhase);

      return currentPhase?.cancellable;
    },
  },
  methods: {
    async pdf() {
      this.loadingPdf = true;
      const baseURL = process.env.NODE_ENV === 'production'
        ? '/public/backend/api/v1/'
        : 'http://localhost:8888/public/backend/api/v1/';
      const options = {
        method: 'GET',
        credentials: 'include',
      };
      const docResponse = await fetch(`${baseURL}dias/real-estate/pdf/${this.$route.params.id}`, options);
      const doc = await docResponse.arrayBuffer();
      console.log(doc, doc.byteLength);
      if (doc.byteLength < 150) {
        this.message = 'Virhe tiedoston lataamisessa DIASista.';
        this.loadingPdf = false;
        return;
      }

      download(doc, `kauppakirja-${this.$route.params.id}`, 'application/pdf');
      this.loadingPdf = false;
    },

    async send() {
      this.saving = true;
      this.$bus.$emit('SHOW_GLOBAL_LOADER', 'Tarkistetaan tietoja');
      this.body.tradeInitiatedTimestamp = new Date().toISOString();
      this.body.attachments.forEach((attachment) => {
        if (attachment.documentType !== 'TRADE_REGISTER_EXTRACT' && attachment.documentType !== 'MINUTES_EXTRACT') {
          delete attachment.businessId; // eslint-disable-line
        }
      });

      this.body.initiatorTradeReferenceId = this.$route.params.id;
      const body = {
        ...this.body,
      };

      if (body.events) {
        delete body.events;
      }

      if (body.tradePhase) {
        delete body.tradePhase;
      }

      if (this.removePledges) {
        delete body.panttikirjojenKoodit;
      }

      // eslint-disable-next-line no-param-reassign
      body.sellers.forEach((seller) => { delete seller.id; });

      // eslint-disable-next-line no-param-reassign
      body.buyers.forEach((buyer) => { delete buyer.id; });

      if (body?.buyersMortgageStatus === 'UNKNOWN') {
        delete body.buyersMortgageStatus;
      }

      const response = await apiPost(`dias/real-estate/draft/${this.$route.params.id}`, body);
      if (response?.result?.data?.description?.fin) {
        this.message = response.result.data.description.fin;
        this.$bus.$emit('HIDE_GLOBAL_LOADER');
      } else {
        this.message = '';
        this.$bus.$emit('REFRESH_SALE_DATA');
      }
      this.saving = false;
    },

    async reset() {
      this.resetting = true;
      await apiPost(`dias/resettrade/${this.$route.params.id}`);
      setTimeout(() => {
        this.resetting = false;
        this.$bus.$emit('EMPTY_SALE_DATA');
      }, 2500);
    },

    async cancel() {
      this.cancelling = true;
      await apiPost(`dias/real-estate/${this.$route.params.id}/cancel`, { initiatorPersonId: this.body.initiatorPersonId, cancellationReason: this.cancelReason });
      setTimeout(() => {
        this.cancelling = false;
        this.cancelDialog = false;
        this.$bus.$emit('EMPTY_SALE_DATA');
      }, 2500);
    },

    async startSigning() {
      this.startingSigning = true;
      await apiPost(`dias/real-estate/${this.$route.params.id}/start-signing`, { initiatorPersonId: this.body.initiatorPersonId });
      setTimeout(() => {
        this.startingSigning = false;
        this.startSigningDialog = false;
        this.$bus.$emit('REFRESH_SALE_DATA');
      }, 2500);
    },

    async submit() {
      this.submitting = true;

      this.message = '';

      const params = {
        automaticSigningProcess: this.automaticSigningProcess,
        initiator: this.body.initiatorPersonId,
      };

      const response = await apiPost(`dias/real-estate/${this.$route.params.id}/submit-for-approvals`, params);

      if (response?.result?.data?.description?.fin) {
        this.message = response.result.data.description.fin;
      }

      setTimeout(() => {
        this.submitting = false;
        this.$bus.$emit('REFRESH_SALE_DATA');
        this.approvalDialog = false;
      }, 2500);
    },
  },
};
</script>
<style lang="scss">
  .action-buttons {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
</style>
